import { useMemo, useState } from 'react'
import { isNotIncludedIn } from 'shared/utils'
import { TableLayout } from './useTableLayouts'

export type UseColumnsProps = {
  tableLayout: Pick<TableLayout, 'columnsLayouts' | 'extraColumns'>
  amountOfActionColumns?: number
}

// As colunas de ação e seleção também devem ser consideradas como colunas fixas e a navegação de colunas deve considerar isso.
export default function useColumns({
  tableLayout: { columnsLayouts, extraColumns },
  amountOfActionColumns = 0
}: UseColumnsProps) {
  const { pinnedColumns, unpinnedVisibleColumnsCount } = useMemo(
    () => ({
      pinnedColumns: columnsLayouts
        .filter((column) => column.freeze)
        .map((column) => column.name),
      unpinnedVisibleColumnsCount: columnsLayouts.filter(
        (column) => !column.freeze
      ).length
    }),
    [columnsLayouts]
  )

  const unpinnedColumns = useMemo(() => {
    const layoutColumnsNames = columnsLayouts.map((column) => column.name)

    const unpinnedLayoutColumns = layoutColumnsNames.filter(
      isNotIncludedIn(pinnedColumns)
    )

    const columnsNotInLayout = extraColumns
      .flatMap((column: string | { field_names: string[] }) =>
        typeof column === 'object' && 'field_names' in column
          ? column.field_names
          : [column]
      )
      .filter(isNotIncludedIn(layoutColumnsNames))

    return [...unpinnedLayoutColumns, ...columnsNotInLayout]
  }, [columnsLayouts, extraColumns, pinnedColumns])

  const [currentColumnIndex, setCurrentColumnIndex] = useState(0)

  const totalColumns =
    pinnedColumns.length + unpinnedColumns.length + amountOfActionColumns

  const amountOfFixedColumns =
    unpinnedVisibleColumnsCount + pinnedColumns.length + amountOfActionColumns

  const displayedColumns = useMemo(() => {
    const startIndex = Math.max(0, currentColumnIndex)

    const endIndex = startIndex + unpinnedVisibleColumnsCount

    const currentUnpinnedColumns = unpinnedColumns.slice(startIndex, endIndex)

    return [...pinnedColumns, ...currentUnpinnedColumns]
  }, [
    currentColumnIndex,
    unpinnedColumns,
    unpinnedVisibleColumnsCount,
    pinnedColumns
  ])

  const onChangeColumnsPage = (value: number) => {
    const currentColumn = value - amountOfFixedColumns

    if (currentColumn < 0) {
      setCurrentColumnIndex(0)
      return
    }

    setCurrentColumnIndex(currentColumn)
  }

  const allColumns = useMemo(
    () => [...pinnedColumns, ...unpinnedColumns],
    [pinnedColumns, unpinnedColumns]
  )

  return {
    displayedColumns,
    onChangeColumnsPage,
    currentColumnIndex,
    totalColumns,
    amountOfFixedColumns,
    allColumns
  }
}
